<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="update-tyre-popup" v-if="popupOpen">
    <div>
      <TableBox
        :tableData="retreadTyreListFiltered"
        title="Retread Tyres List"
        :maxSelection="-1"
        :selectedRow.sync="retreadTyresSelectedRow"
        :hideColumns="updateTyreStatusTableHideColumns"
        :dataType="updateTyreStatusTableDataType"
      />
      <div class="cta-group">
        <div class="w-100">
          <Dropdown
            placeholder="Update Status"
            :selectedObject.sync="selectedAction"
            :items="actionList"
            itemKey="label"
          />
          <Dropdown
            v-if="selectedAction && selectedAction.value === 'warehouse'"
            placeholder="Warehouse"
            :selectedObject.sync="selectedWarehouse"
            :items="warehouseList"
            itemKey="label"
          />
          <div v-if="updateErrorMessage" style="color: red">{{ updateErrorMessage }}</div>
        </div>
        <Button @click="updateClicked">Update</Button>
        <Button @click="$emit('update:popupOpen', false)">Close</Button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    retreadTyreListFiltered() {
      return this.retreadTyreList.map((item, index) => {
        const {
          TyreGuid,
          SerialNo,
          NoOfRetread,
          TyreType,
          TyreIndex,
          JobNo,
          DateChange,
          DateUpdated,
          UserID,
          WhproductTxnid,
        } = item;

        return {
          '#': index + 1,
          'Serial No': SerialNo,
          'Tyre Type': TyreType,
          TyreIndex,
          'No Of Retread': NoOfRetread,
          'Job No': JobNo,
          'Date Change': DateChange,
          'Date Updated': DateUpdated,
          'User ID': UserID,
          'ProdTxnID': WhproductTxnid,
          TyreGuid,
        };
      });
    },
  },
  data: function () {
    return {
      retreadTyreList: [],
      retreadTyresSelectedRow: [],

      actionList: [
        { value: 'warehouse', label: 'Send to Warehouse' },
        { value: 'discard', label: 'Discard' },
      ],
      selectedAction: { value: 'warehouse', label: 'Send to Warehouse' },
      selectedWarehouse: null,
      warehouseList: [],

      updateErrorMessage: '',
      updateLoading: false,

      updateTyreStatusTableHideColumns: ['TyreGuid'],
      updateTyreStatusTableDataType: {
        '#': 'number',
        'Serial No': 'string',
        'Tyre Type': 'string',
        TyreIndex: 'number',
        'No Of Retread': 'number',
        'Job No': 'string',
        'Date Change': 'date',
        'Date Updated': 'datetime',
        'User ID': 'string',
        'ProdTxnID': 'number',
        TyreGuid: 'string',
      },
    };
  },
  props: {
    popupOpen: { type: Boolean, default: false },
  },
  methods: {
    async updateClicked() {
      this.updateErrorMessage = '';

      let error = false;
      if (this.retreadTyresSelectedRow.length < 1) {
        this.$showAlert('error', 'No tyre is selected', 'Please select a tyre to update status');
        error = true;
      }

      if (!this.selectedAction) {
        this.$showAlert('error', 'No action is selected', 'Please select an action to update status');
        error = true;
      } else if (this.selectedAction.value == 'warehouse' && !this.selectedWarehouse) {
        this.$showAlert('error', 'No warehouse is selected', 'Please select a warehouse to send the tyre to');
        error = true;
      }

      if (error) return;

      const selectedTyreGUID = this.retreadTyresSelectedRow.map((item) => {
        return item.TyreGuid;
      });

      const selectedAction = this.selectedAction.value;
      const selectedWarehouse = this.selectedWarehouse ? this.selectedWarehouse['Warehouse Code'] : '';

      this.updateLoading = true;
      this.$fullscreenLoading('Updating Retread Tyres');
      try {
        const response = await this.$apiCallV2('/WorkshopJob/UpdateRetreadTyre', {
          action: selectedAction,
          whsecode: selectedWarehouse,
          tyreguids: selectedTyreGUID,
        });

        const result = Array.isArray(response) ? response[0] : response;
        if (result && result.Results === '1') {
          this.updateErrorMessage = result.ErrMsg || 'Failed to update retread tyres';
          this.$showAlert('error', 'Update Failed', this.updateErrorMessage);
          return;
        }

        const message = result && result.ErrMsg ? result.ErrMsg : 'Retread tyre status updated successfully';
        this.$showAlert('success', 'Update Successful', message);
        this.retreadTyresSelectedRow = [];
        this.clearFields();
        await this.getRetreadTyreList();
      } finally {
        this.updateLoading = false;
        this.$fullscreenLoading();
      }
    },

    async getRetreadTyreList() {
      const result = await this.$apiCallV2('/WorkshopJob/GetRetreadTyreList', {});
      this.retreadTyreList = [];
      await this.$nextTick();
      this.retreadTyreList = result ?? [];
    },

    async getWarehouse() {
      this.warehouseList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WAREHOUSE' })).map(
        (warehouse) => {
          return {
            ...warehouse,
            label: `${warehouse['Warehouse Code']} - ${warehouse.Name}`,
          };
        },
      );
    },

    async apiGets() {
      this.$fullscreenLoading('Getting Retread Tyres');
      await Promise.all([this.getRetreadTyreList(), this.getWarehouse()]);
      this.$fullscreenLoading();
    },

    clearFields() {
      this.selectedAction = this.actionList.find((item) => item.value == 'warehouse') || null;
      this.selectedWarehouse = null;
    },
  },
  mounted() {},
  watch: {
    popupOpen(val) {
      this.clearFields();
      if (val) this.apiGets();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.update-tyre-popup {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  > * {
    height: 100%;
    max-height: 32rem;
    width: 100%;
    max-width: 55rem;
    background-color: white;
    border-radius: 4px;
    padding: 8px;
    overflow: auto;

    display: flex;
    flex-direction: column;

    > .table-box-main {
      min-height: 0;

      margin-bottom: 8px;
    }

    .cta-group {
      display: flex;
      align-items: end;
      gap: 4px;
      background-color: white;
      z-index: 2;
      button {
        height: 28px;
      }
    }
  }
}
</style>
